

import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, Grid, Typography, Avatar, Chip,
  CircularProgress, LinearProgress, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton,
  Tooltip, Alert, TextField, InputAdornment, Snackbar,
  CssBaseline, Button,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import {
  AssignmentInd, TrendingUp, Refresh, Star, Email,
  Search, RocketLaunch, CheckCircle, Send as SendIcon,
  HourglassEmpty, Dashboard as DashboardIcon, Description,
  PersonOutline, BadgeOutlined, Assessment,
  ExitToApp as ExitToAppIcon,
  AccountCircle as AccountCircleIcon,
  Notifications as NotificationsIcon,
} from "@mui/icons-material";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import { useNavigate, useLocation } from "react-router-dom";
import { OnboardingDetail } from "./CandidateBoardingPages/OnboardingPage.js";
import styled, { keyframes, createGlobalStyle, css } from "styled-components";
import axios from "axios";
import recruiterTheme, {
  FONT_BODY, FONT_DISPLAY, TYPE, RADIUS, SHADOW, PAGE_PADDING, COLORS, CONTROL_HEIGHT, ELLIPSIS, humanizeStatus,
} from "../../styles/recruiterTheme";
import ProfileForm from "../shared/Profileform.js";
import BenchPeople from "./BenchPeople/Benchpeople.js";
import Employees from "./EmployeePages/EmployeesPage.js";
import Reports from "./Reports.js";
import OnboardingPage from "./CandidateBoardingPages/OnboardingPage.js"
import AcceptedCandidatesPage from "./OfferReleasePages/Acceptedcandidatespage.js"
import OffboardingPage, { ResignedStrip } from "./CandidateBoardingPages/Offboardingpage.js";


// ── Global Font ───────────────────────────────────────────────────────────────
const GlobalStyle = createGlobalStyle`
  * { box-sizing: border-box; }
`;

// ── Animations ────────────────────────────────────────────────────────────────
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ── Layout Shells ─────────────────────────────────────────────────────────────
const Container = styled.div`
  display: flex;
  background: #F3EEF9;
  min-height: 100vh;
  font-family: ${FONT_BODY};
  padding-top: 72px;
  overflow: hidden;
`;

const CenteredSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow-y: auto;
  height: calc(100vh - 72px);
  background: #F3EEF9;
`;

const ScrollableContent = styled.div`
  width: 100%;
  min-height: 100%;
  padding-bottom: 40px;
`;

// ── Sidebar ───────────────────────────────────────────────────────────────────
const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 300px;
  min-width: 260px;
  flex-shrink: 0;
  background: linear-gradient(170deg, #2D1B5E 0%, #4F378B 55%, #6750A4 100%);
  height: calc(100vh - 72px);
  padding: 24px 16px;
  position: sticky;
  top: 0;
  overflow-y: auto;
  box-shadow: 4px 0 24px rgba(79,55,139,0.18);
  z-index: 10;
`;

const SidebarLabel = styled(Typography)`
  && {
    font-family: ${FONT_BODY};
    font-size: ${TYPE.overline}px;
    font-weight: 700;
    letter-spacing: 0.08em;
    line-height: 1.25;
    text-transform: uppercase;
    color: rgba(255,255,255,0.45);
    margin: 16px 0 8px 12px;
  }
`;

const NavButton = styled(IconButton)`
  && {
    width: 100%;
    border-radius: ${RADIUS.sm}px;
    padding: 10px 12px;
    justify-content: flex-start;
    text-align: left;
    color: rgba(255,255,255,0.82);
    transition: background 0.18s ease, color 0.18s ease;
    margin-bottom: 4px;
    gap: 12px;
    font-family: ${FONT_BODY};
    font-weight: 600;
    font-size: ${TYPE.body}px;
    line-height: 1.25;
    position: relative;
    &:hover {
      background: rgba(255,255,255,0.14);
      color: #fff;
    }
    svg { font-size: 20px; flex-shrink: 0; }
  }
`;

const SidebarDivider = styled.div`
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
  margin: 12px 0;
`;

// ── AppBar ────────────────────────────────────────────────────────────────────
const StyledAppBar = styled.div`
  background: rgba(255,255,255,0.97);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(103,80,164,0.12);
  box-shadow: 0 2px 20px rgba(79,55,139,0.08);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1300;
  display: flex;
  align-items: center;
  min-height: 72px;
  padding: 0 24px;
  justify-content: space-between;
`;

const BrandText = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 800 !important;
    font-size: ${TYPE.section}px !important;
    background: linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: -0.02em;
    line-height: 1.25;
  }
`;

const StyledAvatar = styled.div`
  font-family: ${FONT_BODY};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #7C5CBF, #4F378B);
  color: #fff;
  font-size: ${TYPE.body}px;
  font-weight: 700;
  border-radius: 50%;
  box-shadow: 0 3px 10px rgba(79,55,139,0.35);
  flex-shrink: 0;
`;

const BellButton = styled(IconButton)`
  && {
    background: linear-gradient(135deg, #F3EEF9, #E8DEF8);
    border-radius: ${RADIUS.sm}px;
    width: 40px;
    height: 40px;
    transition: background 0.2s;
    position: relative;
    &:hover {
      background: linear-gradient(135deg, #E8DEF8, #D0BCFF);
    }
    svg { color: #4F378B; font-size: 20px; }
  }
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 16px 4px 4px;
  background: linear-gradient(135deg, #F8F4FF, #EDE7F6);
  border-radius: ${RADIUS.pill}px;
  border: 1px solid rgba(103,80,164,0.15);
  min-width: 160px;
  white-space: nowrap;
`;

// ── Page shell ("cards on canvas") ────────────────────────────────────────────
// Transparent container: every tab sits on the lavender canvas with the same
// padding; sections inside are their own white cards. Embedded tab components
// add no outer padding of their own.
const StyledPaper = styled.div`
  padding: ${PAGE_PADDING}px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  ${css`animation: ${fadeSlideIn} 0.4s ease both;`}
`;

// White card around a table or a section.
const SectionCard = styled.div`
  background: ${COLORS.surface};
  border: 1px solid ${COLORS.border};
  border-radius: ${RADIUS.lg}px;
  box-shadow: ${SHADOW.card};
  overflow: hidden;
  min-width: 0;
  margin-bottom: 24px;
`;

// ── Page Title ────────────────────────────────────────────────────────────────
const PageTitle = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.page}px !important;
    line-height: 1.25 !important;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0 !important;
  }
`;

const PageSubtitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.body}px !important;
    line-height: 1.5 !important;
    color: #8B7DB5 !important;
    font-weight: 500 !important;
    margin: 4px 0 24px !important;
  }
`;

// ── Design tokens ─────────────────────────────────────────────────────────────
const BLUE            = "#1d4ed8";
const BASE            = process.env.REACT_APP_API_BASE_URL;
const ONBOARDING_BASE = process.env.REACT_APP_API_ONBOARDING_URL;

// utils/apiInterceptor.js adds the Bearer token to every fetch.
const authHeaders = () => ({ "Content-Type": "application/json" });

const handle = async (res) => {
  let data = {};
  try { data = await res.json(); } catch { /* empty / non-JSON body */ }
  if (!res.ok) throw new Error(data?.message || data?.detail || `Request failed (${res.status})`);
  return data;
};

// ── Session helpers (same contract as RecruiterDashboard) ─────────────────────
const PERSISTABLE_TABS = new Set([
  "hrDashboard", "candidates", "benchPeople", "employees", "onboarding", "offboarding", "reports", "profile",
]);
const DEFAULT_TAB    = "hrDashboard";
const TAB_KEY        = "activeTab";
const RETURN_TAB_KEY = "returnTab";

const readSession = (key) => {
  try { return sessionStorage.getItem(key); } catch { return null; }
};
const writeSession = (key, value) => {
  try { sessionStorage.setItem(key, value); } catch { /* storage unavailable */ }
};
const removeSession = (key) => {
  try { sessionStorage.removeItem(key); } catch { /* storage unavailable */ }
};
const readStoredUser = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem("rbotUser"));
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const startOnboarding = (candidateId) =>
  fetch(`${ONBOARDING_BASE}/selected-candidates/${candidateId}/start`, {
    method: "POST", headers: authHeaders(),
  }).then(handle);

const releaseOffer = (candidateId) =>
  fetch(`${ONBOARDING_BASE}/selected-candidates/${candidateId}/release-offer`, {
    method: "POST", headers: authHeaders(),
  }).then(handle);


// ── KPI Card ──────────────────────────────────────────────────────────────────
function KPICard({ label, value, sub, icon, accent, loading }) {
  return (
    <Box sx={{
      p: 2, borderRadius: `${RADIUS.md}px`, bgcolor: COLORS.surface,
      border: `1px solid ${COLORS.border}`, height: "100%",
      display: "flex", alignItems: "flex-start", gap: 2,
      position: "relative", overflow: "hidden",
      boxShadow: SHADOW.card,
      transition: "box-shadow 0.2s",
      "&:hover": { boxShadow: SHADOW.cardHover },
      "&::before": {
        content: '""', position: "absolute", top: 0, left: 0, right: 0,
        height: 3, bgcolor: accent,
      },
    }}>
      <Box sx={{ p: 1, bgcolor: `${accent}18`, borderRadius: `${RADIUS.sm}px`, color: accent, display: "flex", flexShrink: 0 }}>
        {icon}
      </Box>
      <Box flex={1} minWidth={0}>
        <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700, color: COLORS.muted, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5 }}>
          {label}
        </Typography>
        {loading
          ? <CircularProgress size={20} sx={{ color: accent, mt: 0.5 }} />
          : <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.page, fontWeight: 800, color: COLORS.primaryDeep, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>{value ?? 0}</Typography>
        }
        {sub && <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, color: COLORS.faint, mt: 0.5 }}>{sub}</Typography>}
      </Box>
    </Box>
  );
}

// ── Status Chips ──────────────────────────────────────────────────────────────
const pillSx = (c) => ({
  bgcolor: c.bg, color: c.color, fontWeight: 700, height: 22, borderRadius: `${RADIUS.pill}px`,
  fontFamily: FONT_BODY, "& .MuiChip-label": { fontSize: TYPE.overline, px: 1 },
});

const ONBOARDING_STATUS_COLORS = {
  "Not Started":   { color: "#991b1b", bg: "#fef2f2" },
  "Pending Setup": { color: "#c2410c", bg: "#fff7ed" },
  "Initiated":     { color: "#854d0e", bg: "#fef9c3" },
  "In Progress":   { color: BLUE,      bg: "#eff6ff" },
  "Completed":     { color: "#166534", bg: "#dcfce7" },
};

function OnboardingStatusChip({ status }) {
  const c = ONBOARDING_STATUS_COLORS[status] || ONBOARDING_STATUS_COLORS["Not Started"];
  return <Chip label={humanizeStatus(status || "Not Started")} size="small" sx={pillSx(c)} />;
}

const OFFER_STATUS_COLORS = {
  "Not Sent": { bg: "#f1f5f9", color: "#64748b" },
  "Sent":     { bg: "#eff6ff", color: "#1d4ed8" },
  "Accepted": { bg: "#dcfce7", color: "#166534" },
  "Declined": { bg: "#fee2e2", color: "#991b1b" },
};

function OfferStatusChip({ status }) {
  const c = OFFER_STATUS_COLORS[status] || OFFER_STATUS_COLORS["Not Sent"];
  return <Chip label={humanizeStatus(status || "Not Sent")} size="small" sx={pillSx(c)} />;
}

const BGV_COLORS = {
  "Not Initiated": { bg: "#f1f5f9", color: "#64748b" },
  "Initiated":     { bg: "#eff6ff", color: "#1d4ed8" },
  "In Progress":   { bg: "#fef9c3", color: "#854d0e" },
  "Completed":     { bg: "#dcfce7", color: "#166534" },
  "Failed":        { bg: "#fee2e2", color: "#991b1b" },
};

function BGVStatusChip({ status }) {
  const sc = BGV_COLORS[status || "Not Initiated"] || BGV_COLORS["Not Initiated"];
  return <Chip label={humanizeStatus(status || "Not Initiated")} size="small" sx={pillSx(sc)} />;
}

const headCellSx = {
  fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700, color: COLORS.muted,
  textTransform: "uppercase", letterSpacing: "0.08em", py: 1.5,
  borderBottom: `1px solid ${COLORS.border}`, whiteSpace: "nowrap",
};

const cardHeaderSx = {
  px: 3, py: 2, borderBottom: `1px solid ${COLORS.border}`,
  display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2,
};

const cardTitleSx = { fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.cardTitle, color: COLORS.primaryDeep };
const cardSubSx   = { fontFamily: FONT_BODY, fontSize: TYPE.caption, color: COLORS.muted, mt: 0.5 };

// ════════════════════════════════════════════════════════════════════════════════
export default function HRDashboard() {
  const navigate    = useNavigate();
  const location    = useLocation();

  // ── Identity: navigation state (fresh login) or, after a refresh, the
  // profile Login.js persisted in localStorage (rbotUser).
  const currentUser = useMemo(
    () => (location.state?.email ? location.state : readStoredUser()) || {},
    [location.state]
  );
  const { email, name, phone, userType, _id } = currentUser;
  const userData = useMemo(
    () => (email && userType ? { name: name || "", phone, email, userType, _id } : null),
    [name, phone, email, userType, _id]
  );
  const fullName = (name || "").trim() || "HR User";
  const initials = fullName.split(/\s+/).filter(Boolean).map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "HR";

  // ── State ────────────────────────────────────────────────────────────────────
  const [activeTab,          setActiveTab]          = useState(() => {
    const saved = readSession(TAB_KEY);
    return saved && PERSISTABLE_TABS.has(saved) ? saved : DEFAULT_TAB;
  });
  const [tabRefreshKey,      setTabRefreshKey]      = useState(0);
  const [loading,            setLoading]            = useState(true);
  const [loadError,          setLoadError]          = useState("");
  const [error,              setError]              = useState("");
  const [search,             setSearch]             = useState("");
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [selectedKpis,       setSelectedKpis]       = useState({});
  const [starting,           setStarting]           = useState(null);
  const [detailOpen,         setDetailOpen]         = useState(false);
  const [activeEmp,          setActiveEmp]          = useState(null);
  const [snack,              setSnack]              = useState({ open: false, message: "" });
  const [releasing,          setReleasing]          = useState(null);

  const logoutApiUrl = process.env.REACT_APP_LOGOUT_API;

  // ── Guard: only redirect when there is no user at all ───────────────────────
  useEffect(() => {
    if (!email || !userType) navigate("/login", { replace: true });
  }, [email, userType, navigate]);

  // Persist the current tab so F5 restores it.
  useEffect(() => {
    if (PERSISTABLE_TABS.has(activeTab)) writeSession(TAB_KEY, activeTab);
  }, [activeTab]);

  // ── Fetch selected candidates ─────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res  = await fetch(`${BASE}/onboarding/selected-candidates`, { headers: authHeaders() });
      const data = await handle(res);
      setSelectedCandidates(Array.isArray(data?.data) ? data.data : []);
      setSelectedKpis(data?.kpis || {});
    } catch (err) {
      setLoadError(err?.message || "Failed to load selected candidates");
    } finally {
      setLoading(false);
    }
  }, []);

  // Dashboard data loads on first mount (if restored there), on every switch to
  // the tab, and when the same sidebar item is clicked again (tabRefreshKey).
  useEffect(() => {
    if (activeTab === "hrDashboard") fetchData();
  }, [activeTab, tabRefreshKey, fetchData]);

  // ── Tab switching (replaces the old window.location.reload) ─────────────────
  const switchTab = useCallback((tab) => {
    setDetailOpen(false);
    setActiveEmp(null);
    setError("");
    removeSession(RETURN_TAB_KEY);
    setActiveTab(tab);
    // Remounts self-loading tab components even when the same item is clicked again.
    setTabRefreshKey((k) => k + 1);
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────────
  const handleOpenOnboarding = async (candidate) => {
    if (!candidate?._id || starting) return;
    setStarting(candidate._id);
    setError("");
    try {
      const res = await startOnboarding(candidate._id);
      if (!res?.employee?._id) throw new Error("Onboarding record was not returned by the server");
      setActiveEmp(res.employee);
      setDetailOpen(true);
    } catch (err) {
      setError(err?.message || "Could not start onboarding");
    } finally {
      setStarting(null);
    }
  };

  const handleSaveSuccess = useCallback((msg = "Successfully updated") =>
    setSnack({ open: true, message: msg }), []);

  const handleDetailClose = () => {
    setDetailOpen(false);
    setActiveEmp(null);
    fetchData();
  };

  const handleReleaseOffer = async (e, candidate) => {
    e.stopPropagation();
    if (releasing) return;
    const label = candidate.candidatename || "this candidate";
    if (!window.confirm(`Release offer to ${label}${candidate.candidateEmail ? ` (${candidate.candidateEmail})` : ""}?`)) return;
    setReleasing(candidate._id);
    setError("");
    try {
      await releaseOffer(candidate._id);
      handleSaveSuccess(`Offer sent to ${label}`);
      fetchData();
    } catch (err) {
      setError(err?.message || "Failed to release offer");
    } finally {
      setReleasing(null);
    }
  };

  const handleProfileTabClick = useCallback(() => switchTab("profile"), [switchTab]);

  const handleLogout = useCallback(async () => {
    const em = email || localStorage.getItem("email");
    let tokens = {};
    try {
      tokens = JSON.parse(localStorage.getItem("userTokens")) || {};
    } catch {
      tokens = {};
    }
    const storedToken = localStorage.getItem("token");
    const token = (em && tokens[em]) || (storedToken && storedToken !== "null" ? storedToken : null);

    try {
      if (token) {
        await axios.post(logoutApiUrl, { token, email: em }, { headers: { Authorization: `Bearer ${token}` } });
      }
    } catch (err) {
      console.error("[HRDashboard] Logout failed:", err.message);
    } finally {
      if (em) delete tokens[em];
      localStorage.setItem("userTokens", JSON.stringify(tokens));
      localStorage.removeItem("token");
      localStorage.removeItem("email");
      localStorage.removeItem("rbotUser");
      removeSession(TAB_KEY);
      removeSession(RETURN_TAB_KEY);
      navigate("/login", { replace: true });
    }
  }, [email, logoutApiUrl, navigate]);

  // ── Filter ────────────────────────────────────────────────────────────────────
  const filtered = selectedCandidates.filter((c) => {
    const q = search.trim().toLowerCase();
    return (
      !q ||
      c.candidatename?.toLowerCase().includes(q) ||
      c.candidateEmail?.toLowerCase().includes(q) ||
      c.jobRole?.toLowerCase().includes(q) ||
      c.companyName?.toLowerCase().includes(q) ||
      String(c.jdID ?? "").toLowerCase().includes(q)
    );
  });

  // BGV pipeline counts come from each candidate's bgv_status (the API's kpis
  // object counts onboarding status, not BGV).
  const bgvCounts = useMemo(() => {
    const counts = { notInitiated: 0, inProgress: 0, completed: 0, failed: 0 };
    selectedCandidates.forEach((c) => {
      const s = c?.bgv_status || "Not Initiated";
      if (s === "Completed") counts.completed += 1;
      else if (s === "Failed") counts.failed += 1;
      else if (s === "Initiated" || s === "In Progress") counts.inProgress += 1;
      else counts.notInitiated += 1;
    });
    return counts;
  }, [selectedCandidates]);

  const navStyle = (tab) => ({
    background: activeTab === tab ? "rgba(255,255,255,0.18)" : "transparent",
  });

  const iconBtnSx = {
    border: `1px solid ${COLORS.border}`, borderRadius: `${RADIUS.sm}px`, bgcolor: COLORS.surface,
    color: COLORS.muted, width: CONTROL_HEIGHT.md, height: CONTROL_HEIGHT.md,
    "&:hover": { bgcolor: COLORS.background, borderColor: COLORS.primary, color: COLORS.primary },
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <ThemeProvider theme={recruiterTheme}>
      <GlobalStyle />
      <Container>
        <CssBaseline />

        {/* ══ AppBar ══════════════════════════════════════════════════════════ */}
        <StyledAppBar>
          {/* Brand */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px", paddingRight: "24px", borderRight: "1.5px solid rgba(103,80,164,0.1)" }}>
            <Box sx={{ width: 48, height: 48, borderRadius: `${RADIUS.md}px`, background: "linear-gradient(135deg, #EDE7F6, #D0BCFF)", border: "2px solid rgba(103,80,164,0.18)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(79,55,139,0.18)", flexShrink: 0 }}>
              <AssignmentInd sx={{ color: COLORS.primary, fontSize: 28 }} />
            </Box>
            <Box>
              <BrandText>Resourcing Bot</BrandText>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700, color: COLORS.muted, letterSpacing: "0.08em", textTransform: "uppercase", lineHeight: 1.25, mt: "4px" }}>
                HR Portal
              </Typography>
            </Box>
          </Box>

          {/* Right side */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <BellButton aria-label="Notifications">
              <NotificationsIcon />
            </BellButton>
            <UserCard>
              <StyledAvatar>{initials}</StyledAvatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography title={fullName} sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.body, color: COLORS.primaryDeep, lineHeight: 1.25, maxWidth: 200, ...ELLIPSIS }}>
                  {fullName}
                </Typography>
                <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 500, fontSize: TYPE.caption, color: COLORS.muted, lineHeight: 1.25 }}>
                  HR
                </Typography>
              </Box>
            </UserCard>
          </Box>
        </StyledAppBar>

        {/* ══ Sidebar ═════════════════════════════════════════════════════════ */}
        <MenuContainer>

          <SidebarLabel>HR Tools</SidebarLabel>

          <NavButton onClick={() => switchTab("hrDashboard")} style={navStyle("hrDashboard")}>
            <DashboardIcon />
            <span>Dashboard</span>
          </NavButton>

          <NavButton onClick={() => switchTab("candidates")} style={navStyle("candidates")}>
            <Description />
            <span>Candidates</span>
          </NavButton>

          <NavButton onClick={() => switchTab("benchPeople")} style={navStyle("benchPeople")}>
            <PersonOutline />
            <span>Bench People</span>
          </NavButton>

          <NavButton onClick={() => switchTab("employees")} style={navStyle("employees")}>
            <BadgeOutlined />
            <span>Employees</span>
          </NavButton>

          <NavButton onClick={() => switchTab("onboarding")} style={navStyle("onboarding")}>
            <AssignmentInd />
            <span>Onboarding</span>
          </NavButton>
          <NavButton onClick={() => switchTab("offboarding")} style={navStyle("offboarding")}>
            <PersonRemoveIcon />
            <span>Offboarding</span>
          </NavButton>

          <SidebarDivider />

          <SidebarLabel>Reporting</SidebarLabel>

          <NavButton onClick={() => switchTab("reports")} style={navStyle("reports")}>
            <Assessment />
            <span>Reports</span>
          </NavButton>

          <SidebarDivider />

          <SidebarLabel>Account</SidebarLabel>

          <NavButton onClick={handleProfileTabClick} style={navStyle("profile")}>
            <AccountCircleIcon />
            <span>Profile</span>
          </NavButton>

          <NavButton onClick={handleLogout}>
            <ExitToAppIcon />
            <span>Logout</span>
          </NavButton>

        </MenuContainer>

        {/* ══ Main content ════════════════════════════════════════════════════ */}
        <CenteredSection>
          <ScrollableContent>

            {/* ── HR Dashboard tab ── */}
            {activeTab === "hrDashboard" && (
              <StyledPaper>

                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
                  <Box>
                    <PageTitle>HR Dashboard</PageTitle>
                    <PageSubtitle>
                      {selectedCandidates.length} selected candidate{selectedCandidates.length !== 1 ? "s" : ""}
                      &nbsp;·&nbsp; Onboarding pipeline &nbsp;·&nbsp; BGV tracking
                    </PageSubtitle>
                  </Box>
                  <Tooltip title="Refresh">
                    <span>
                      <IconButton onClick={fetchData} disabled={loading} size="small" sx={iconBtnSx} aria-label="Refresh">
                        <Refresh fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>

                {error && (
                  <Alert severity="error" onClose={() => setError("")} sx={{ mb: 3, borderRadius: `${RADIUS.sm}px` }}>
                    {error}
                  </Alert>
                )}

                {/* KPI Strip */}
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={6} md={3}>
                    <KPICard label="Total Selected" value={selectedCandidates.length} icon={<Star sx={{ fontSize: 20 }} />} accent="#7c3aed" sub="From ResourcingBot" loading={loading} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <KPICard label="Not Started" value={selectedKpis.not_started} icon={<AssignmentInd sx={{ fontSize: 20 }} />} accent="#dc2626" sub="Onboarding pending" loading={loading} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <KPICard label="In Progress" value={selectedKpis.in_progress} icon={<TrendingUp sx={{ fontSize: 20 }} />} accent={BLUE} sub="Currently onboarding" loading={loading} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <KPICard label="Completed" value={selectedKpis.completed} icon={<CheckCircle sx={{ fontSize: 20 }} />} accent="#059669" sub="Fully onboarded" loading={loading} />
                  </Grid>
                </Grid>

                {/* Onboarding Queue */}
                <SectionCard>

                  {/* Table toolbar */}
                  <Box sx={cardHeaderSx}>
                    <Box>
                      <Typography sx={cardTitleSx}>Onboarding Queue</Typography>
                      <Typography sx={cardSubSx}>Click a row to manage onboarding details</Typography>
                    </Box>
                    <TextField
                      placeholder="Search by name, email, role…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      size="small"
                      sx={{ minWidth: 280, "& .MuiOutlinedInput-root": { height: CONTROL_HEIGHT.md, borderRadius: `${RADIUS.sm}px`, bgcolor: COLORS.surface, "& fieldset": { borderColor: COLORS.border }, "&:hover fieldset": { borderColor: COLORS.primary }, "&.Mui-focused fieldset": { borderColor: COLORS.primary } } }}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: COLORS.faint }} /></InputAdornment> }}
                    />
                  </Box>

                  {/* Table body */}
                  {loading ? (
                    <Box display="flex" flexDirection="column" alignItems="center" py={8} gap={1.5}>
                      <CircularProgress size={32} sx={{ color: COLORS.primary }} />
                      <Typography sx={{ fontSize: TYPE.small, color: COLORS.muted }}>Loading selected candidates…</Typography>
                    </Box>
                  ) : loadError ? (
                    <Box display="flex" flexDirection="column" alignItems="center" py={8} gap={1.5}>
                      <Typography sx={{ fontSize: TYPE.body, fontWeight: 600, color: COLORS.error }}>{loadError}</Typography>
                      <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={fetchData}
                        sx={{ height: CONTROL_HEIGHT.sm, textTransform: "none", fontWeight: 600, borderRadius: `${RADIUS.sm}px` }}>
                        Retry
                      </Button>
                    </Box>
                  ) : filtered.length === 0 ? (
                    <Box display="flex" flexDirection="column" alignItems="center" py={8} gap={1}>
                      <Box sx={{ width: 56, height: 56, borderRadius: "50%", bgcolor: COLORS.background, display: "flex", alignItems: "center", justifyContent: "center", mb: 1 }}>
                        <Star sx={{ fontSize: 28, color: COLORS.faint }} />
                      </Box>
                      <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.body, color: COLORS.primaryDeep }}>
                        {search ? "No candidates match your search" : "No selected candidates yet"}
                      </Typography>
                      <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, color: COLORS.muted }}>
                        Selected candidates from ResourcingBot will appear here
                      </Typography>
                    </Box>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: COLORS.surfaceTint }}>
                            {["Candidate", "Role / Company", "BGV", "Offer", "Onboarding", "Progress", "Actions"].map((h) => (
                              <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filtered.map((c) => {
                            const isCompleted = c.onboarding_status === "Completed";
                            const pct = Number(c.onboarding_pct) || 0;
                            return (
                              <TableRow
                                key={c._id}
                                hover
                                onClick={() => handleOpenOnboarding(c)}
                                sx={{ cursor: starting ? "progress" : "pointer", opacity: isCompleted ? 0.75 : 1, "&:last-child td": { borderBottom: 0 }, "& td": { borderBottom: `1px solid ${COLORS.border}`, py: 1.5 } }}
                              >
                                {/* Candidate */}
                                <TableCell sx={{ maxWidth: 260 }}>
                                  <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                                    <Avatar sx={{ width: 32, height: 32, fontSize: TYPE.small, fontWeight: 700, background: "linear-gradient(135deg, #2D1B5E, #6750A4)" }}>
                                      {(c.candidatename || "?")[0].toUpperCase()}
                                    </Avatar>
                                    <Box minWidth={0}>
                                      <Typography title={c.candidatename} sx={{ fontSize: TYPE.small, fontWeight: 700, color: COLORS.primaryDeep, ...ELLIPSIS }}>{c.candidatename || "—"}</Typography>
                                      <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
                                        <Email sx={{ fontSize: TYPE.caption, color: COLORS.faint, flexShrink: 0 }} />
                                        <Typography title={c.candidateEmail} sx={{ fontSize: TYPE.caption, color: COLORS.muted, ...ELLIPSIS }}>{c.candidateEmail || "—"}</Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                </TableCell>

                                {/* Role / Company */}
                                <TableCell sx={{ maxWidth: 200 }}>
                                  <Typography title={c.jobRole} sx={{ fontSize: TYPE.small, fontWeight: 600, color: COLORS.primaryDeep, ...ELLIPSIS }}>{c.jobRole || "—"}</Typography>
                                  <Typography title={c.companyName} sx={{ fontSize: TYPE.caption, color: COLORS.muted, ...ELLIPSIS }}>{c.companyName || "—"}</Typography>
                                </TableCell>

                                <TableCell sx={{ whiteSpace: "nowrap" }}><BGVStatusChip status={c.bgv_status} /></TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}><OfferStatusChip status={c.offer_status} /></TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}><OnboardingStatusChip status={c.onboarding_status} /></TableCell>

                                {/* Progress */}
                                <TableCell sx={{ minWidth: 110 }}>
                                  {c.is_employee ? (
                                    <Box>
                                      <Typography sx={{ fontSize: TYPE.caption, color: COLORS.muted, mb: 0.5, fontVariantNumeric: "tabular-nums" }}>{pct}%</Typography>
                                      <LinearProgress
                                        variant="determinate"
                                        value={Math.min(Math.max(pct, 0), 100)}
                                        sx={{ height: 6, borderRadius: 3, bgcolor: COLORS.border, "& .MuiLinearProgress-bar": { bgcolor: pct === 100 ? "#16a34a" : COLORS.primary, borderRadius: 3 } }}
                                      />
                                    </Box>
                                  ) : (
                                    <Typography sx={{ fontSize: TYPE.caption, color: COLORS.faint, whiteSpace: "nowrap" }}>Not started</Typography>
                                  )}
                                </TableCell>

                                {/* Actions */}
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                  <Box display="flex" gap={1} alignItems="center">

                                    {(!c.offer_status || c.offer_status === "Not Sent") && (
                                      <Tooltip title="Release offer">
                                        <span>
                                          <IconButton
                                            size="small"
                                            disabled={!!releasing}
                                            onClick={(e) => handleReleaseOffer(e, c)}
                                            sx={{ color: COLORS.primary, bgcolor: COLORS.background, "&:hover": { bgcolor: COLORS.border }, borderRadius: `${RADIUS.sm}px` }}
                                          >
                                            {releasing === c._id ? <CircularProgress size={14} /> : <SendIcon sx={{ fontSize: 16 }} />}
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    )}

                                    {c.offer_status === "Sent" && (
                                      <Tooltip title="Waiting for candidate response">
                                        <Box sx={{ width: 32, height: 32, borderRadius: `${RADIUS.sm}px`, bgcolor: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                          <HourglassEmpty sx={{ fontSize: 16, color: BLUE }} />
                                        </Box>
                                      </Tooltip>
                                    )}

                                    <Tooltip title={isCompleted ? "View onboarding" : "Start onboarding"}>
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={!!starting}
                                          onClick={() => handleOpenOnboarding(c)}
                                          sx={{ color: isCompleted ? "#059669" : COLORS.primary, bgcolor: isCompleted ? "#dcfce7" : COLORS.background, "&:hover": { bgcolor: isCompleted ? "#bbf7d0" : COLORS.border }, borderRadius: `${RADIUS.sm}px` }}
                                        >
                                          {starting === c._id
                                            ? <CircularProgress size={14} />
                                            : isCompleted
                                              ? <CheckCircle sx={{ fontSize: 16 }} />
                                              : <RocketLaunch sx={{ fontSize: 16 }} />
                                          }
                                        </IconButton>
                                      </span>
                                    </Tooltip>

                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {/* Footer */}
                  {!loading && !loadError && filtered.length > 0 && (
                    <Box sx={{ px: 3, py: 1.5, borderTop: `1px solid ${COLORS.border}`, bgcolor: COLORS.surfaceTint }}>
                      <Typography sx={{ fontSize: TYPE.caption, color: COLORS.muted }}>
                        Showing <strong style={{ color: COLORS.ink }}>{filtered.length}</strong> of <strong style={{ color: COLORS.ink }}>{selectedCandidates.length}</strong> candidates
                      </Typography>
                    </Box>
                  )}
                </SectionCard>

                {/* ── Resigned / Exiting Employees strip ── */}
                <ResignedStrip key={tabRefreshKey} />

                {/* BGV Pipeline */}
                <SectionCard style={{ marginBottom: 0 }}>
                  <Box sx={cardHeaderSx}>
                    <Box>
                      <Typography sx={cardTitleSx}>BGV Pipeline</Typography>
                      <Typography sx={cardSubSx}>Background verification status across all selected candidates</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ p: 3 }}>
                    <Grid container spacing={2}>
                      {[
                        { label: "Not Initiated", count: bgvCounts.notInitiated, accent: "#dc2626", bg: "#fef2f2" },
                        { label: "In Progress",   count: bgvCounts.inProgress,   accent: "#d97706", bg: "#fef9c3" },
                        { label: "Completed",     count: bgvCounts.completed,    accent: "#059669", bg: "#dcfce7" },
                        { label: "Failed",        count: bgvCounts.failed,       accent: "#991b1b", bg: "#fee2e2" },
                      ].map((s) => (
                        <Grid item xs={6} md={3} key={s.label}>
                          <Box sx={{ p: 2, borderRadius: `${RADIUS.md}px`, border: `1px solid ${COLORS.border}`, bgcolor: s.bg, textAlign: "center", position: "relative", overflow: "hidden", "&::before": { content: '""', position: "absolute", top: 0, left: 0, right: 0, height: 3, bgcolor: s.accent } }}>
                            {loading
                              ? <CircularProgress size={24} sx={{ color: s.accent }} />
                              : <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.stat, fontWeight: 800, color: s.accent, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>{s.count}</Typography>}
                            <Typography sx={{ fontSize: TYPE.caption, color: COLORS.muted, mt: 0.5, fontWeight: 600 }}>
                              {s.label}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </SectionCard>

              </StyledPaper>
            )}

            {/* ── Candidates tab ── */}
            {activeTab === "candidates" && (
              <StyledPaper>
                <AcceptedCandidatesPage key={tabRefreshKey} />
              </StyledPaper>
            )}

            {/* ── Bench People tab ── */}
            {activeTab === "benchPeople" && (
              <StyledPaper>
                <BenchPeople key={tabRefreshKey} />
              </StyledPaper>
            )}

            {/* ── Employees tab ── */}
            {activeTab === "employees" && (
              <StyledPaper>
                <Employees key={tabRefreshKey} />
              </StyledPaper>
            )}

            {/* ── Onboarding tab ── */}
            {activeTab === "onboarding" && (
              <StyledPaper>
                <OnboardingPage key={tabRefreshKey} onSaveSuccess={handleSaveSuccess} />
              </StyledPaper>
            )}

            {/* ── Offboarding tab ── */}
            {activeTab === "offboarding" && (
              <StyledPaper>
                <OffboardingPage key={tabRefreshKey} onSaveSuccess={handleSaveSuccess} />
              </StyledPaper>
            )}

            {/* ── Reports tab ── */}
            {activeTab === "reports" && (
              <StyledPaper>
                <Reports key={tabRefreshKey} />
              </StyledPaper>
            )}

            {/* ── Profile tab: ProfileForm brings its own page padding ── */}
            {activeTab === "profile" && <ProfileForm key={tabRefreshKey} userData={userData} />}

          </ScrollableContent>
        </CenteredSection>

        {/* ── Onboarding Detail Dialog ── */}
        <OnboardingDetail
          open={detailOpen}
          onClose={handleDetailClose}
          employee={activeEmp}
          onSaveSuccess={handleSaveSuccess}
        />

        {/* ── Snackbar ── */}
        <Snackbar
          open={snack.open}
          autoHideDuration={3000}
          onClose={() => setSnack({ open: false, message: "" })}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnack({ open: false, message: "" })}
            severity="success"
            variant="filled"
            sx={{ borderRadius: `${RADIUS.sm}px`, fontWeight: 600, bgcolor: COLORS.primary }}
          >
            {snack.message}
          </Alert>
        </Snackbar>

      </Container>
    </ThemeProvider>
  );
}
